import { useCallback, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
// From packages/shared, not the generated client: swagger can't encode a discriminated union.
import type {
	PostDatingCompleteResult,
	PostDatingPrepareResult,
} from '@dealstack/shared/lib/common/eideasy/postDating';
import {
	buildEndpointBase,
	ensureError,
	isUserInitiatedError,
	makeEidEasyClient,
	toErrorKey,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useEidEasySign.utils';
import type {
	ApplyDateProgress,
	UseApplyDateOptions,
	UseApplyDateResult,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useApplyDate.types';

interface ApplyDatePrepareResponse {
	payload: PostDatingPrepareResult;
}

interface ApplyDateCompleteResponse {
	payload: PostDatingCompleteResult;
}

/** Attempts per placement before the loop stops and leaves the rest for a later run. */
const MAX_ATTEMPTS_PER_PLACEMENT = 3;

/**
 * The image eID Easy draws when it renders its own handwritten block, so it has to be the date
 * itself. Where the custom-signature-image webhook answers, it serves this same PNG.
 */
const dateImageDataUrl = (stampImageBase64: string): string =>
	`data:image/png;base64,${stampImageBase64}`;

/**
 * Drives applying a date to every placement on a document, one PAdES revision each.
 *
 * No credential is collected: the visual is a date, the server renders it, and eID Easy's
 * handwritten action takes it as an argument — so there is nothing for the applier to draw or
 * confirm. Authorisation is the EDITOR permission the endpoint already checks.
 *
 * A loop rather than one session (cf. `useEidEasySignCore`, which is single-shot): each
 * placement's revision must be computed against the file the previous one produced, so they
 * cannot be batched the way bulk signing batches files. The server's ledger decides which
 * placement is next, not any state here, so a resumed run just calls prepare again.
 */
export const useApplyDate = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	onApplied,
}: UseApplyDateOptions): UseApplyDateResult => {
	const [status, setStatus] = useState<EidEasySignStatus>(EidEasySignStatus.IDLE);
	const [error, setError] = useState<string | null>(null);
	const [progress, setProgress] = useState<ApplyDateProgress | null>(null);

	/** Server-reported placements outstanding; the loop requires it to strictly decrease. */
	const lastRemainingRef = useRef(Number.POSITIVE_INFINITY);

	const basePath = buildEndpointBase(dealId, programmeId, documentWorkflowInstanceId);

	const fail = useCallback((err: unknown, phase: string) => {
		if (!isUserInitiatedError(err)) {
			Sentry.captureException(err, { tags: { signingFlow: 'apply-date', phase } });
		}
		setError(toErrorKey(err));
		setStatus(EidEasySignStatus.ERROR);
	}, []);

	const prepare = useCallback(
		async (effectiveDate: string): Promise<PostDatingPrepareResult> => {
			const result = await makeRequest<ApplyDatePrepareResponse>({
				endpoint: `${basePath}/apply-date/prepare`,
				method: HTTP_METHOD.POST,
				body: JSON.stringify({ effectiveDate }),
			});
			return result.payload;
		},
		[basePath]
	);

	const complete = useCallback(
		async (docId: string): Promise<PostDatingCompleteResult> => {
			const result = await makeRequest<ApplyDateCompleteResponse>({
				endpoint: `${basePath}/apply-date/complete`,
				method: HTTP_METHOD.POST,
				body: JSON.stringify({ docId }),
			});
			return result.payload;
		},
		[basePath]
	);

	/** Records a landed revision and reports whether the document still has placements left. */
	const advance = useCallback((placementTotal: number, remainingCount: number): boolean => {
		setProgress({ done: placementTotal - remainingCount, total: placementTotal });
		if (remainingCount === 0) {
			return false;
		}
		// An unmoved count means nothing landed. Throwing here surfaces as a failed attempt
		// rather than looping forever on the same placement.
		if (remainingCount >= lastRemainingRef.current) {
			throw new Error(
				`Applying the date made no progress: ${remainingCount} of ${placementTotal} placements still remaining`
			);
		}
		lastRemainingRef.current = remainingCount;
		return true;
	}, []);

	const finish = useCallback(
		(placementTotal: number) => {
			setProgress({ done: placementTotal, total: placementTotal });
			setStatus(EidEasySignStatus.DONE);
			onApplied();
		},
		[onApplied]
	);

	const start = useCallback(
		async (effectiveDate: string) => {
			lastRemainingRef.current = Number.POSITIVE_INFINITY;
			setProgress(null);
			setError(null);

			for (;;) {
				let attempt = 0;
				for (;;) {
					attempt++;
					try {
						setStatus(EidEasySignStatus.PREPARING);
						const prepared = await prepare(effectiveDate);
						if (prepared.status === 'ALREADY_DATED') {
							finish(prepared.placementTotal);
							return;
						}

						// Deliberately still PREPARING, not AWAITING_SIGN: that step tells the user
						// to confirm on their device, and there is nothing to confirm — the
						// launcher posts the date image and returns.
						if (!prepared.mockMode) {
							await new Promise<void>((resolve, reject) => {
								makeEidEasyClient(prepared).signature.handwrittenSignature.start({
									handwrittenSignatureDataUrl: dateImageDataUrl(
										prepared.stampImageBase64
									),
									success: () => resolve(),
									fail: (failError: unknown) => reject(ensureError(failError)),
								});
							});
						}

						setStatus(EidEasySignStatus.COMPLETING);
						const completed = await complete(prepared.docId);

						if (!advance(prepared.placementTotal, completed.remainingCount)) {
							finish(prepared.placementTotal);
							return;
						}
						break; // this placement landed — move on to the next one
					} catch (err: unknown) {
						if (attempt >= MAX_ATTEMPTS_PER_PLACEMENT) {
							fail(err, 'apply-date');
							return;
						}
					}
				}
			}
		},
		[prepare, complete, advance, finish, fail]
	);

	const reset = useCallback(() => {
		lastRemainingRef.current = Number.POSITIVE_INFINITY;
		setProgress(null);
		setError(null);
		setStatus(EidEasySignStatus.IDLE);
	}, []);

	return { start, reset, status, error, progress };
};
